import type { ReactNode } from 'react';

import { cva, cx } from 'cva';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import {
  ListBox,
  ListBoxItem,
  type ListBoxItemProps,
  type ListBoxProps,
} from 'react-aria-components/ListBox';

import { Icon } from '../icon/icon';
import { Text, type TextProps } from '../text/text';

import styles from './dropdown_list_box.module.css';

type DropdownListBoxSize = 's' | 'm' | 'l';

const dropdownListBoxVariants = cva({
  base: styles.dropdown_list_box,
  variants: {
    size: {
      s: styles.size_s,
      m: styles.size_m,
      l: styles.size_l,
    },
  },
  defaultVariants: {
    size: 'm',
  },
});

type DropdownListBoxProps<T extends object> = ListBoxProps<T> & {
  size?: DropdownListBoxSize;
};

/**
 * DropdownListBox is a styled React Aria `ListBox` variant intended for
 * popovers and dropdown-style option lists.
 *
 * See https://react-spectrum.adobe.com/react-aria/ListBox.html for the
 * underlying implementation.
 */
export function DropdownListBox<T extends object>({
  className,
  size,
  ...props
}: DropdownListBoxProps<T>) {
  const classes = dropdownListBoxVariants({ size, className });

  return <ListBox<T> {...props} className={classes} />;
}

/**
 * DropdownListBoxItemText renders high-emphasis label text for a list box item.
 */
export function DropdownListBoxItemText(props: TextProps) {
  return (
    <Text
      elementType="p"
      slot="label"
      color="high_emphasis"
      variant="value"
      {...props}
    />
  );
}

/**
 * DropdownListBoxItemDescription renders medium-emphasis description text for
 * a list box item.
 */
export function DropdownListBoxItemDescription(props: TextProps) {
  return (
    <Text
      elementType="p"
      slot="description"
      color="medium_emphasis"
      variant="label"
      {...props}
    />
  );
}

/**
 * DropdownListBoxItem renders an individual option with a trailing selection
 * indicator.
 */
export function DropdownListBoxItem<T extends object>({
  className,
  ...props
}: ListBoxItemProps<T>) {
  const classes = cx(styles.dropdown_list_box_item, className);

  return (
    <ListBoxItem<T> {...props} className={classes}>
      {composeRenderProps(props.children, (children, { isSelected }) => (
        <>
          {children}
          {isSelected ? (
            <span className={styles.selection_indicator}>
              <Icon name="checkmark" />
            </span>
          ) : null}
        </>
      ))}
    </ListBoxItem>
  );
}

type DropdownListBoxEmptyStateProps = {
  children: ReactNode;
};

/**
 * DropdownListBoxEmptyState renders fallback content when no list box items
 * are available.
 */
export function DropdownListBoxEmptyState({
  children,
}: DropdownListBoxEmptyStateProps) {
  return (
    <div
      className={cx(
        styles.dropdown_list_box_item,
        styles.dropdown_list_box_empty_state,
      )}
    >
      {children}
    </div>
  );
}
